/**
 * @engine/vfx — VFX preset builders (effects group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts to
 * keep that module focused on content-type / importer / serialization.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Black Hole - Multi-system effect with accretion disk, relativistic jets, and Hawking radiation.
 *
 * Systems:
 * 1. Accretion Disk - Particles spiral inward with vortex motion, HDR orange/white colors
 * 2. Jet Up - Relativistic jet shooting upward, purple/blue HDR
 * 3. Jet Down - Relativistic jet shooting downward
 * 4. Event Horizon - Dark core particles, very dim
 * 5. Hawking Radiation - Rare bright escaping particles
 */

export function createBlackHoleVFX(name: string = "Black Hole"): VFXGraphData {
  const template: VFXGraphData = {
    id: "blackhole_effect",
    name: "Black Hole",
    nodes: [
      // === ACCRETION DISK SYSTEM ===
      {
        id: "spawn_disk",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 800 },
        contextId: "ctx_spawn_disk",
      },
      {
        id: "init_disk_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 2.5, max: 4.0 },
        contextId: "ctx_init_disk",
      },
      {
        id: "init_disk_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 180,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_disk",
      },
      {
        id: "init_disk_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 0, z: 0 }, speed: 0, spread: 0 },
        contextId: "ctx_init_disk",
      },
      {
        id: "init_disk_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 2, max: 4 },
        contextId: "ctx_init_disk",
      },
      {
        id: "init_disk_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 4, g: 1.5, b: 0.3, a: 1 } },
              { t: 0.3, color: { r: 3.5, g: 0.8, b: 0.2, a: 1 } },
              { t: 0.6, color: { r: 5, g: 2, b: 0.5, a: 1 } },
              { t: 1, color: { r: 3, g: 0.5, b: 0.1, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_disk",
      },
      {
        id: "init_disk_angular",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 550 },
        inputValues: { speed: 180, variance: 90 },
        contextId: "ctx_init_disk",
      },
      {
        id: "update_disk_vortex",
        type: "vfx.update.vortex",
        position: { x: 50, y: 50 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          axis: { x: 0, y: 0, z: 1 },
          strength: 100,
          pullStrength: 40,
        },
        contextId: "ctx_update_disk",
      },
      {
        id: "update_disk_attract",
        type: "vfx.update.attract",
        position: { x: 50, y: 150 },
        inputValues: {
          target: { x: 0, y: 0, z: 0 },
          strength: 30,
          maxDistance: 250,
          falloff: "quadratic",
        },
        contextId: "ctx_update_disk",
      },
      {
        id: "update_disk_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 3, g: 0.8, b: 0.2, a: 0.8 } },
              { t: 0.3, color: { r: 5, g: 1.5, b: 0.3, a: 1 } },
              { t: 0.5, color: { r: 8, g: 3, b: 0.8, a: 1 } },
              { t: 0.7, color: { r: 12, g: 8, b: 4, a: 1 } },
              { t: 0.85, color: { r: 20, g: 18, b: 15, a: 1 } },
              { t: 1, color: { r: 25, g: 25, b: 25, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_disk",
      },
      {
        id: "update_disk_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.4, v: 1.2 },
              { t: 0.7, v: 2.5 },
              { t: 0.9, v: 4 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_disk",
      },
      {
        id: "update_disk_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 450 },
        inputValues: {
          intensity: 15,
          frequency: 2,
          octaves: 2,
          roughness: 0.5,
        },
        contextId: "ctx_update_disk",
      },
      {
        id: "output_disk",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_disk",
      },

      // === JET UP SYSTEM ===
      {
        id: "spawn_jet_up",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 150 },
        contextId: "ctx_spawn_jet_up",
      },
      {
        id: "init_jet_up_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 1.0, max: 2.0 },
        contextId: "ctx_init_jet_up",
      },
      {
        id: "init_jet_up_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 15,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_jet_up",
      },
      {
        id: "init_jet_up_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 300, spread: 8 },
        contextId: "ctx_init_jet_up",
      },
      {
        id: "init_jet_up_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 3, max: 6 },
        contextId: "ctx_init_jet_up",
      },
      {
        id: "init_jet_up_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 8, g: 6, b: 12, a: 1 } },
        contextId: "ctx_init_jet_up",
      },
      {
        id: "update_jet_up_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 15, g: 12, b: 20, a: 1 } },
              { t: 0.3, color: { r: 10, g: 6, b: 15, a: 0.9 } },
              { t: 0.7, color: { r: 5, g: 3, b: 8, a: 0.6 } },
              { t: 1, color: { r: 2, g: 1, b: 4, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_jet_up",
      },
      {
        id: "update_jet_up_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.5 },
              { t: 0.2, v: 1 },
              { t: 0.6, v: 1.5 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_jet_up",
      },
      {
        id: "update_jet_up_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 250 },
        inputValues: { coefficient: 0.3 },
        contextId: "ctx_update_jet_up",
      },
      {
        id: "output_jet_up",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_jet_up",
      },

      // === JET DOWN SYSTEM ===
      {
        id: "spawn_jet_down",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 150 },
        contextId: "ctx_spawn_jet_down",
      },
      {
        id: "init_jet_down_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 1.0, max: 2.0 },
        contextId: "ctx_init_jet_down",
      },
      {
        id: "init_jet_down_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 15,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_jet_down",
      },
      {
        id: "init_jet_down_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: {
          direction: { x: 0, y: -1, z: 0 },
          speed: 300,
          spread: 8,
        },
        contextId: "ctx_init_jet_down",
      },
      {
        id: "init_jet_down_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 3, max: 6 },
        contextId: "ctx_init_jet_down",
      },
      {
        id: "init_jet_down_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 8, g: 6, b: 12, a: 1 } },
        contextId: "ctx_init_jet_down",
      },
      {
        id: "update_jet_down_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 15, g: 12, b: 20, a: 1 } },
              { t: 0.3, color: { r: 10, g: 6, b: 15, a: 0.9 } },
              { t: 0.7, color: { r: 5, g: 3, b: 8, a: 0.6 } },
              { t: 1, color: { r: 2, g: 1, b: 4, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_jet_down",
      },
      {
        id: "update_jet_down_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.5 },
              { t: 0.2, v: 1 },
              { t: 0.6, v: 1.5 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_jet_down",
      },
      {
        id: "update_jet_down_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 250 },
        inputValues: { coefficient: 0.3 },
        contextId: "ctx_update_jet_down",
      },
      {
        id: "output_jet_down",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_jet_down",
      },

      // === EVENT HORIZON (DARK CORE) SYSTEM ===
      {
        id: "spawn_core",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 100 },
        contextId: "ctx_spawn_core",
      },
      {
        id: "init_core_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.5, max: 1.0 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 25,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 0, z: 0 }, speed: 0, spread: 0 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 15, max: 30 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 0.02, g: 0.01, b: 0.03, a: 1 } },
        contextId: "ctx_init_core",
      },
      {
        id: "update_core_vortex",
        type: "vfx.update.vortex",
        position: { x: 50, y: 50 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          axis: { x: 0, y: 0, z: 1 },
          strength: 200,
          pullStrength: 80,
        },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.3, v: 0.9 },
              { t: 0.7, v: 0.9 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_core",
      },
      {
        id: "output_core",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_core",
      },

      // === HAWKING RADIATION SYSTEM ===
      {
        id: "spawn_hawking",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 5 },
        contextId: "ctx_spawn_hawking",
      },
      {
        id: "init_hawking_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 3, max: 5 },
        contextId: "ctx_init_hawking",
      },
      {
        id: "init_hawking_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 35,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_hawking",
      },
      {
        id: "init_hawking_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: {
          direction: { x: 1, y: 0, z: 0 },
          speed: 80,
          spread: 180,
        },
        contextId: "ctx_init_hawking",
      },
      {
        id: "init_hawking_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 1, max: 2 },
        contextId: "ctx_init_hawking",
      },
      {
        id: "init_hawking_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 30, g: 30, b: 35, a: 1 } },
        contextId: "ctx_init_hawking",
      },
      {
        id: "update_hawking_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 30, g: 28, b: 35, a: 1 } },
              { t: 0.3, color: { r: 20, g: 18, b: 25, a: 0.9 } },
              { t: 0.7, color: { r: 10, g: 8, b: 15, a: 0.5 } },
              { t: 1, color: { r: 2, g: 2, b: 5, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_hawking",
      },
      {
        id: "update_hawking_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.5, v: 1.5 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_hawking",
      },
      {
        id: "output_hawking",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_hawking",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      // Disk
      {
        id: "ctx_spawn_disk",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_disk"],
        collapsed: false,
      },
      {
        id: "ctx_init_disk",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 600 },
        nodeIds: [
          "init_disk_lifetime",
          "init_disk_position",
          "init_disk_velocity",
          "init_disk_size",
          "init_disk_color",
          "init_disk_angular",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_disk",
        type: "vfx.update",
        position: { x: 50, y: 850 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_disk_vortex",
          "update_disk_attract",
          "update_disk_color",
          "update_disk_size",
          "update_disk_turbulence",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_disk",
        type: "vfx.output",
        position: { x: 50, y: 1400 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_disk"],
        collapsed: false,
      },
      // Jet Up
      {
        id: "ctx_spawn_jet_up",
        type: "vfx.spawn",
        position: { x: 400, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_jet_up"],
        collapsed: false,
      },
      {
        id: "ctx_init_jet_up",
        type: "vfx.initialize",
        position: { x: 400, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_jet_up_lifetime",
          "init_jet_up_position",
          "init_jet_up_velocity",
          "init_jet_up_size",
          "init_jet_up_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_jet_up",
        type: "vfx.update",
        position: { x: 400, y: 750 },
        size: { width: 280, height: 300 },
        nodeIds: [
          "update_jet_up_color",
          "update_jet_up_size",
          "update_jet_up_drag",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_jet_up",
        type: "vfx.output",
        position: { x: 400, y: 1100 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_jet_up"],
        collapsed: false,
      },
      // Jet Down
      {
        id: "ctx_spawn_jet_down",
        type: "vfx.spawn",
        position: { x: 750, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_jet_down"],
        collapsed: false,
      },
      {
        id: "ctx_init_jet_down",
        type: "vfx.initialize",
        position: { x: 750, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_jet_down_lifetime",
          "init_jet_down_position",
          "init_jet_down_velocity",
          "init_jet_down_size",
          "init_jet_down_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_jet_down",
        type: "vfx.update",
        position: { x: 750, y: 750 },
        size: { width: 280, height: 300 },
        nodeIds: [
          "update_jet_down_color",
          "update_jet_down_size",
          "update_jet_down_drag",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_jet_down",
        type: "vfx.output",
        position: { x: 750, y: 1100 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_jet_down"],
        collapsed: false,
      },
      // Core
      {
        id: "ctx_spawn_core",
        type: "vfx.spawn",
        position: { x: 1100, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_core"],
        collapsed: false,
      },
      {
        id: "ctx_init_core",
        type: "vfx.initialize",
        position: { x: 1100, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_core_lifetime",
          "init_core_position",
          "init_core_velocity",
          "init_core_size",
          "init_core_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_core",
        type: "vfx.update",
        position: { x: 1100, y: 750 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_core_vortex", "update_core_alpha"],
        collapsed: false,
      },
      {
        id: "ctx_output_core",
        type: "vfx.output",
        position: { x: 1100, y: 1000 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_core"],
        collapsed: false,
      },
      // Hawking
      {
        id: "ctx_spawn_hawking",
        type: "vfx.spawn",
        position: { x: 1450, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_hawking"],
        collapsed: false,
      },
      {
        id: "ctx_init_hawking",
        type: "vfx.initialize",
        position: { x: 1450, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_hawking_lifetime",
          "init_hawking_position",
          "init_hawking_velocity",
          "init_hawking_size",
          "init_hawking_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_hawking",
        type: "vfx.update",
        position: { x: 1450, y: 750 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_hawking_color", "update_hawking_size"],
        collapsed: false,
      },
      {
        id: "ctx_output_hawking",
        type: "vfx.output",
        position: { x: 1450, y: 1000 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_hawking"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      // Disk flow
      {
        id: "flow_disk_1",
        fromContextId: "ctx_spawn_disk",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_disk",
        toPortId: "spawn_event",
      },
      {
        id: "flow_disk_2",
        fromContextId: "ctx_init_disk",
        fromPortId: "particle",
        toContextId: "ctx_update_disk",
        toPortId: "particle",
      },
      {
        id: "flow_disk_3",
        fromContextId: "ctx_update_disk",
        fromPortId: "particle",
        toContextId: "ctx_output_disk",
        toPortId: "particle",
      },
      // Jet Up flow
      {
        id: "flow_jet_up_1",
        fromContextId: "ctx_spawn_jet_up",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_jet_up",
        toPortId: "spawn_event",
      },
      {
        id: "flow_jet_up_2",
        fromContextId: "ctx_init_jet_up",
        fromPortId: "particle",
        toContextId: "ctx_update_jet_up",
        toPortId: "particle",
      },
      {
        id: "flow_jet_up_3",
        fromContextId: "ctx_update_jet_up",
        fromPortId: "particle",
        toContextId: "ctx_output_jet_up",
        toPortId: "particle",
      },
      // Jet Down flow
      {
        id: "flow_jet_down_1",
        fromContextId: "ctx_spawn_jet_down",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_jet_down",
        toPortId: "spawn_event",
      },
      {
        id: "flow_jet_down_2",
        fromContextId: "ctx_init_jet_down",
        fromPortId: "particle",
        toContextId: "ctx_update_jet_down",
        toPortId: "particle",
      },
      {
        id: "flow_jet_down_3",
        fromContextId: "ctx_update_jet_down",
        fromPortId: "particle",
        toContextId: "ctx_output_jet_down",
        toPortId: "particle",
      },
      // Core flow
      {
        id: "flow_core_1",
        fromContextId: "ctx_spawn_core",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_core",
        toPortId: "spawn_event",
      },
      {
        id: "flow_core_2",
        fromContextId: "ctx_init_core",
        fromPortId: "particle",
        toContextId: "ctx_update_core",
        toPortId: "particle",
      },
      {
        id: "flow_core_3",
        fromContextId: "ctx_update_core",
        fromPortId: "particle",
        toContextId: "ctx_output_core",
        toPortId: "particle",
      },
      // Hawking flow
      {
        id: "flow_hawking_1",
        fromContextId: "ctx_spawn_hawking",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_hawking",
        toPortId: "spawn_event",
      },
      {
        id: "flow_hawking_2",
        fromContextId: "ctx_init_hawking",
        fromPortId: "particle",
        toContextId: "ctx_update_hawking",
        toPortId: "particle",
      },
      {
        id: "flow_hawking_3",
        fromContextId: "ctx_update_hawking",
        fromPortId: "particle",
        toContextId: "ctx_output_hawking",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init_disk: { capacity: 5000, boundsMode: "auto" },
      ctx_init_jet_up: { capacity: 500, boundsMode: "auto" },
      ctx_init_jet_down: { capacity: 500, boundsMode: "auto" },
      ctx_init_core: { capacity: 200, boundsMode: "auto" },
      ctx_init_hawking: { capacity: 50, boundsMode: "auto" },
    },
    capacity: { maxParticles: 7000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Tornado - Spiral particle effect using orbit + curl noise.
 * Demonstrates: velocity_cone, orbit, curl_noise, limit_velocity
 */
